import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Flag, ThumbsDown, ThumbsUp, ChevronUp, ChevronDown, Bot, User, MoreHorizontal, Loader2, ArrowLeft, Reply, X } from "lucide-react";
import Markdown from "@/components/Markdown";
import { api, timeAgo, parseApiError } from "@/lib/api";
import { tid } from "@/lib/testids";
import { useAuth } from "@/context/AuthContext";
import InlineImageAttach from "@/components/InlineImageAttach";
import VoiceButton from "@/components/VoiceButton";
import { useSpeechToText } from "@/hooks/useSpeechToText";
import Seo from "@/components/Seo";
import ShareButtons from "@/components/ShareButtons";
import Breadcrumbs from "@/components/Breadcrumbs";
import RelatedQuestions from "@/components/RelatedQuestions";
import { useNavigate } from "react-router-dom";

const REPORT_REASONS = ["Spam", "Harassment", "Misinformation", "Other", "Hate", "Sexual", "Threats"];

function flattenAnswers(tree) {
  const out = [];
  const walk = (nodes, depth = 1) => {
    (nodes || []).forEach((n) => {
      out.push({ ...n, depth });
      walk(n.replies || [], depth + 1);
    });
  };
  walk(tree);
  return out;
}

function ExpandableText({ text, maxLength = 180, className = "" }) {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);
  
  useEffect(() => {
    if (!expanded) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (!entry.isIntersecting) setExpanded(false); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [expanded]);

  if (!text || text.length <= maxLength) return <span className={className}>{text}</span>;

  return (
    <span ref={ref} className={className}>
      {expanded ? (
        <>
          {text}
          <button onClick={() => setExpanded(false)} className="ml-1 inline-flex items-center text-blue-600 hover:underline">
            <ChevronUp className="h-3 w-3" />
          </button>
        </>
      ) : (
        <>
          {text.slice(0, maxLength).trim()}
          <button onClick={() => setExpanded(true)} className="ml-0.5 inline-flex items-center gap-0.5 text-blue-600 hover:underline">
            <MoreHorizontal className="h-3 w-3" />
          </button>
        </>
      )}
    </span>
  );
}

// Collapsible AI Answer - Hides when community has answered, expandable
function CollapsibleAIAnswer({ aiAnswer, humanAnswerCount }) {
  const [expanded, setExpanded] = useState(false);
  
  // Don't show if no AI answer
  if (!aiAnswer) return null;
  
  // If there are 2+ human answers with engagement, collapse by default
  const hasEnoughHumanAnswers = humanAnswerCount >= 2;
  
  // Truncate text for preview
  const previewLength = 150;
  const fullText = aiAnswer.answer_text || "";
  const needsTruncation = fullText.length > previewLength;
  const previewText = needsTruncation ? fullText.slice(0, previewLength).trim() : fullText;
  
  // If plenty of human answers, show minimal collapsed version
  if (hasEnoughHumanAnswers && !expanded) {
    return (
      <div className="mt-4">
        <button
          onClick={() => setExpanded(true)}
          className="flex items-center gap-2 text-xs text-slate-500 hover:text-indigo-400 transition-colors"
        >
          <Bot className="h-3.5 w-3.5" />
          <span>View AI-assisted response</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-5">
      <Card className="border-slate-700/50 bg-slate-800/30 p-4 backdrop-blur-sm">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500/20">
              <Bot className="h-3.5 w-3.5 text-indigo-400" />
            </div>
            <span className="text-sm font-medium text-slate-400">AI-assisted response</span>
          </div>
          {hasEnoughHumanAnswers && (
            <button
              onClick={() => setExpanded(false)}
              className="text-xs text-slate-500 hover:text-indigo-400 flex items-center gap-1"
            >
              Collapse <ChevronUp className="h-3 w-3" />
            </button>
          )}
        </div>
        
        <div className="text-sm text-slate-300 leading-relaxed">
          {expanded || !needsTruncation ? (
            <>
              <div className="prose prose-sm prose-invert max-w-none">
                <Markdown>{fullText}</Markdown>
              </div>
              {needsTruncation && expanded && (
                <button
                  onClick={() => setExpanded(false)}
                  className="mt-2 text-xs text-indigo-400 hover:underline flex items-center gap-1"
                >
                  Show less <ChevronUp className="h-3 w-3" />
                </button>
              )}
            </>
          ) : (
            <>
              <p>{previewText}...</p>
              <button
                onClick={() => setExpanded(true)}
                className="mt-1 text-xs text-indigo-400 hover:underline flex items-center gap-1"
              >
                Read more <ChevronDown className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
        
        {!hasEnoughHumanAnswers && (
          <p className="mt-3 text-[11px] text-slate-500">
            Generated while community answers arrive
          </p>
        )}
      </Card>
    </div>
  );
}

function AnswerCard({ answer, canInteract, onVote, onReport, onReact, onReply, isNew }) {
  const score = (answer.upvotes || 0) - (answer.downvotes || 0);
  const indent = Math.min((answer.depth - 1) * 16, 64);
  const reactions = answer.reactions || {};
  const isAI = answer.is_ai;
  const canReply = canInteract && answer.depth < 3; // Max depth is 3
  
  // Generate a consistent color based on pseudonym for AI personas - Dark theme
  const getAvatarColor = (name) => {
    const colors = [
      "bg-blue-500/20 text-blue-400",
      "bg-green-500/20 text-green-400", 
      "bg-purple-500/20 text-purple-400",
      "bg-orange-500/20 text-orange-400",
      "bg-pink-500/20 text-pink-400",
      "bg-teal-500/20 text-teal-400",
      "bg-indigo-500/20 text-indigo-400",
      "bg-rose-500/20 text-rose-400",
    ];
    const hash = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div style={{ marginLeft: indent }} className={`transition-all duration-500 ${isNew ? "animate-fadeIn" : ""}`}>
      <Card data-testid={`answer-card-${answer.answer_id}`} className="border-slate-700/50 bg-slate-800/50 p-3 backdrop-blur-sm">
        <div className="flex items-start gap-3">
          <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${answer.user_picture ? "" : getAvatarColor(answer.pseudonym)}`}>
            {answer.user_picture ? (
              <img alt="" src={answer.user_picture} className="h-full w-full rounded-full object-cover" />
            ) : (
              <span className="text-xs font-semibold">
                {String(answer.pseudonym || "U").slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-200">
                {answer.pseudonym ? answer.pseudonym.replace(/_/g, " ") : "Anonymous"}
              </span>
              {/* Author type badge */}
              {isAI ? (
                <Badge className="bg-indigo-500/20 text-indigo-400 border-0 text-[10px] px-1.5 py-0 h-4">
                  <Bot className="h-2.5 w-2.5 mr-0.5" />
                  AI
                </Badge>
              ) : (
                <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-[10px] px-1.5 py-0 h-4">
                  <User className="h-2.5 w-2.5 mr-0.5" />
                  Member
                </Badge>
              )}
              <span className="text-[10px] text-slate-500">{timeAgo(answer.created_at)}</span>
            </div>

            <div className="mt-1.5 text-sm leading-relaxed text-slate-400">
              <ExpandableText text={answer.answer_text} maxLength={200} />
            </div>

            {answer.image_urls?.length > 0 && (
              <div className="mt-2 flex gap-2">
                {answer.image_urls.slice(0, 3).map((u, i) => (
                  <div key={i} className="h-14 w-14 overflow-hidden rounded-lg border border-slate-700">
                    <img alt="" src={u} className="h-full w-full object-cover" />
                  </div>
                ))}
              </div>
            )}

            <div className="mt-2.5 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => canInteract && onVote(answer.answer_id, answer.viewer_vote === "upvote" ? "none" : "upvote")}
                  disabled={!canInteract}
                  className={`rounded p-1 transition-colors ${
                    answer.viewer_vote === "upvote" ? "bg-emerald-500/20 text-emerald-400" : "text-slate-500 hover:bg-slate-700"
                  } disabled:opacity-40`}
                >
                  <ThumbsUp className="h-3.5 w-3.5" />
                </button>
                <span className={`min-w-[18px] text-center text-xs font-medium ${score > 0 ? "text-emerald-400" : score < 0 ? "text-red-400" : "text-slate-500"}`}>
                  {score}
                </span>
                <button
                  onClick={() => canInteract && onVote(answer.answer_id, answer.viewer_vote === "downvote" ? "none" : "downvote")}
                  disabled={!canInteract}
                  className={`rounded p-1 transition-colors ${
                    answer.viewer_vote === "downvote" ? "bg-red-500/20 text-red-400" : "text-slate-500 hover:bg-slate-700"
                  } disabled:opacity-40`}
                >
                  <ThumbsDown className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="flex items-center gap-1">
                {["helpful", "relatable", "funny"].map((r) => (
                  <button
                    key={r}
                    onClick={() => canInteract && onReact(answer.answer_id, answer.viewer_reaction === r ? null : r)}
                    disabled={!canInteract}
                    className={`rounded px-2 py-0.5 text-[10px] transition-colors ${
                      answer.viewer_reaction === r
                        ? "bg-indigo-500/20 text-indigo-400"
                        : "bg-slate-700/50 text-slate-400 hover:bg-slate-700"
                    } disabled:opacity-40`}
                  >
                    {r === "helpful" ? "Helpful" : r === "relatable" ? "Relatable" : "Funny"} {reactions[r] || ""}
                  </button>
                ))}
              </div>

              {/* Reply Button */}
              {canReply && (
                <button 
                  onClick={() => onReply(answer)}
                  className="flex items-center gap-1 rounded px-2 py-0.5 text-[10px] text-slate-500 hover:bg-indigo-500/20 hover:text-indigo-400 transition-colors"
                  data-testid={`reply-btn-${answer.answer_id}`}
                >
                  <Reply className="h-3 w-3" />
                  Reply
                </button>
              )}

              {canInteract && !isAI && (
                <button onClick={() => onReport("answer", answer.answer_id)} className="text-[10px] text-slate-500 hover:text-red-400">
                  <Flag className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function QuestionPage() {
  const { slug } = useParams();
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const seenAnswerIds = useRef(new Set());
  const replyInputRef = useRef(null);

  const [question, setQuestion] = useState(null);
  const [answersTree, setAnswersTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAnswerIds, setNewAnswerIds] = useState(new Set());

  const [answerText, setAnswerText] = useState("");
  const [answerImages, setAnswerImages] = useState([]);
  const [posting, setPosting] = useState(false);
  const [answerDragActive, setAnswerDragActive] = useState(false);

  // Reply state
  const [replyingTo, setReplyingTo] = useState(null); // { answer_id, pseudonym }

  const [reportOpen, setReportOpen] = useState(false);
  const [reportTargetType, setReportTargetType] = useState("answer");
  const [reportTargetId, setReportTargetId] = useState(null);
  const [reportReason, setReportReason] = useState(REPORT_REASONS[0]);
  const [reportDetails, setReportDetails] = useState("");

  const stt = useSpeechToText();
  const canInteract = status === "authed" && user?.pseudonym;
  const answerList = useMemo(() => flattenAnswers(answersTree), [answersTree]);

  const onLogin = () => {
    // Store current path to return to after login
    sessionStorage.setItem('loginReturnUrl', window.location.pathname);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const updateAnswers = useCallback(async (isInitial = false) => {
    try {
      const aRes = await api.get(`/questions/${slug}/answers`);
      const items = aRes.data.items || [];
      
      if (isInitial) {
        setAnswersTree(items);
        flattenAnswers(items).forEach(a => seenAnswerIds.current.add(a.answer_id));
        return;
      }

      const flatNew = flattenAnswers(items);
      const newIds = new Set();
      
      flatNew.forEach(a => {
        if (!seenAnswerIds.current.has(a.answer_id)) {
          newIds.add(a.answer_id);
          seenAnswerIds.current.add(a.answer_id);
        }
      });

      if (newIds.size > 0) {
        setNewAnswerIds(newIds);
        setAnswersTree(items);
        setTimeout(() => setNewAnswerIds(new Set()), 3000);
      } else {
        setAnswersTree(items);
      }
    } catch {}
  }, [slug]);

  const fetchQuestion = useCallback(async () => {
    try {
      const qRes = await api.get(`/questions/${slug}`);
      setQuestion(qRes.data);
    } catch {}
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchQuestion(), updateAnswers(true)]).finally(() => setLoading(false));
    const id = setInterval(() => updateAnswers(false), 25000);
    return () => clearInterval(id);
  }, [slug, status, fetchQuestion, updateAnswers]);

  const postAnswer = async () => {
    setPosting(true);
    try {
      await api.post(`/questions/${slug}/answers`, {
        answer_text: answerText,
        image_urls: answerImages.length > 0 ? answerImages : undefined,
        parent_answer_id: replyingTo?.answer_id || undefined,
      });
      setAnswerText("");
      setAnswerImages([]);
      setReplyingTo(null);
      await updateAnswers(false);
    } catch (e) {
      alert(parseApiError(e, "Failed to post answer."));
    } finally {
      setPosting(false);
    }
  };

  const startReply = (answer) => {
    setReplyingTo({ answer_id: answer.answer_id, pseudonym: answer.pseudonym });
    setAnswerText("");
    // Scroll to and focus the answer input
    setTimeout(() => {
      replyInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      replyInputRef.current?.focus();
    }, 100);
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setAnswerText("");
  };

  const vote = async (answerId, voteType) => {
    try {
      await api.post(`/answers/${answerId}/vote`, { vote_type: voteType });
      await updateAnswers(false);
    } catch (e) {
      alert(parseApiError(e, "Voting failed."));
    }
  };

  const react = async (answerId, reaction) => {
    try {
      await api.post(`/answers/${answerId}/react`, { reaction });
      await updateAnswers(false);
    } catch (e) {
      alert(parseApiError(e, "Reaction failed."));
    }
  };

  const openReport = (type, id) => {
    setReportTargetType(type);
    setReportTargetId(id);
    setReportReason(REPORT_REASONS[0]);
    setReportDetails("");
    setReportOpen(true);
  };

  const submitReport = async () => {
    try {
      if (reportTargetType === "question") {
        await api.post(`/questions/${slug}/report`, { reason: reportReason, details: reportDetails || undefined });
      } else {
        await api.post(`/answers/${reportTargetId}/report`, { reason: reportReason, details: reportDetails || undefined });
      }
      setReportOpen(false);
      alert("Reported. Thanks for keeping the community safe!");
    } catch (e) {
      alert(parseApiError(e, "Report failed."));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!question) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center bg-slate-900 min-h-screen">
        <h1 className="text-xl font-semibold text-slate-200">Question not found</h1>
        <Button onClick={() => navigate("/")} className="mt-4 bg-indigo-600 hover:bg-indigo-500 text-white">
          Go Home
        </Button>
      </div>
    );
  }

  const aiAnswer = answerList.find(a => a.is_ai && (a.ai_label === "AI-assisted response" || a.pseudonym === "AI"));
  const humanAnswers = answerList.filter(a => !a.is_ai || (a.ai_label !== "AI-assisted response" && a.pseudonym !== "AI"));

  const shareUrl = `${window.location.origin}/q/${slug}`;
  const shareTitle = question.title || question.body?.slice(0, 60) + "...";
  
  // Breadcrumbs data
  const breadcrumbItems = [
    { name: question.intent || "Questions", url: `/browse?category=${encodeURIComponent(question.intent || "")}` },
    { name: question.title || question.body?.slice(0, 40) + "...", url: `/q/${slug}` }
  ];

  return (
    <div data-testid={tid.questionPage} className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 pb-24 md:pb-8">
      <div className="mx-auto max-w-3xl px-4 py-6">
        <Seo 
          title={question.title || question.body?.slice(0, 55)} 
          description={question.body?.slice(0, 130) + " – AskIncog Bangalore"} 
          url={`/q/${slug}`}
          image={question.image_urls?.[0] || undefined}
          question={question}
          answers={answerList}
          category={question.intent}
          dateCreated={question.created_at}
          breadcrumbs={[
            { name: "Home", url: "/" },
            ...breadcrumbItems
          ]}
        />

        {/* Breadcrumbs */}
        <Breadcrumbs items={breadcrumbItems} />

        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)} 
          className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-300 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        {/* Question Card */}
        <Card className="border-slate-700/50 bg-slate-800/50 backdrop-blur-sm p-5 shadow-lg">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              {question.title && (
                <h1 className="text-lg font-semibold text-slate-100 sm:text-xl">{question.title}</h1>
              )}
              <div className={`${question.title ? "mt-2" : ""} text-sm leading-relaxed text-slate-400`}>
                <ExpandableText text={question.body} maxLength={400} />
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge className="rounded-full bg-indigo-500/20 text-indigo-300 border-0">{question.intent}</Badge>
                {question.tags?.map((t) => (
                  <Badge key={t} className="rounded-full bg-slate-700 text-slate-300 border-0">#{t}</Badge>
                ))}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Asked anonymously · {timeAgo(question.created_at)}
                {question.location && ` · ${question.location}`}
              </div>

              {question.image_urls?.length > 0 && (
                <div className="mt-3 flex gap-2">
                  {question.image_urls.map((u, i) => {
                    // Fix image URL - handle relative paths
                    const imageUrl = u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
                    return (
                      <div key={i} className="h-20 w-20 overflow-hidden rounded-lg border border-slate-700 bg-slate-800">
                        <img 
                          alt="" 
                          src={imageUrl} 
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=80";
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <ShareButtons 
                url={shareUrl} 
                title={shareTitle} 
                description={question.body?.slice(0, 200)}
                showLabel={false}
              />
            {canInteract && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => openReport("question", question.question_id)} 
                className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
              >
                <Flag className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* AI Response - Collapsible, hidden when community has answered */}
      <CollapsibleAIAnswer 
        aiAnswer={aiAnswer} 
        humanAnswerCount={humanAnswers.length}
      />

      {/* Community Answers */}
      <div className="mt-6">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-indigo-400" />
            <span className="text-sm font-medium text-slate-300">Community Answers</span>
            <Badge className="rounded-full bg-indigo-500/20 text-indigo-300 border-0">{humanAnswers.length}</Badge>
          </div>
        </div>

        <div className="space-y-2">
          {humanAnswers.length === 0 ? (
            <Card className="border-slate-700/50 bg-slate-800/30 p-6 text-center text-sm text-slate-500">
              No community answers yet. Be the first to share your perspective!
            </Card>
          ) : (
            humanAnswers.map((a) => (
              <AnswerCard
                key={a.answer_id}
                answer={a}
                canInteract={canInteract}
                onVote={vote}
                onReport={openReport}
                onReact={react}
                onReply={startReply}
                isNew={newAnswerIds.has(a.answer_id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Answer Form */}
      <div className="mt-6" ref={replyInputRef}>
        {status !== "authed" ? (
          <Card className="border-slate-700/50 bg-slate-800/30 p-4 text-center">
            <p className="text-sm text-slate-400 mb-2">You can ask anonymously, but answering requires login.</p>
            <p className="text-xs text-slate-500 mb-3">This helps keep answers trustworthy.</p>
            <Button onClick={onLogin} className="mt-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 btn-press">
              Login to Answer
            </Button>
          </Card>
        ) : !user?.pseudonym ? (
          <Card className="border-slate-700/50 bg-slate-800/30 p-4 text-center">
            <p className="text-sm text-slate-400">Set your pseudonym in profile to answer</p>
          </Card>
        ) : (
          <Card className={`border-slate-700/50 bg-slate-800/50 p-4 backdrop-blur-sm ${replyingTo ? "ring-2 ring-indigo-500" : ""}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-300">
                  {replyingTo ? "Your Reply" : "Your Answer"}
                </span>
                {replyingTo && (
                  <div className="flex items-center gap-1 rounded-full bg-indigo-500/20 px-2 py-0.5">
                    <Reply className="h-3 w-3 text-indigo-400" />
                    <span className="text-xs text-indigo-400">
                      Replying to {replyingTo.pseudonym?.replace(/_/g, " ") || "someone"}
                    </span>
                    <button 
                      onClick={cancelReply} 
                      className="ml-1 rounded-full p-0.5 hover:bg-indigo-500/30"
                      data-testid="cancel-reply-btn"
                    >
                      <X className="h-3 w-3 text-indigo-400" />
                    </button>
                  </div>
                )}
              </div>
              <span className="text-xs text-slate-500">{answerText.length}/2000</span>
            </div>

            <div className={`mt-2 rounded-lg border ${answerDragActive ? "border-indigo-500" : "border-slate-700"} bg-slate-900/50 transition-all focus-within:border-indigo-500`}>
              <Textarea
                data-testid={tid.answerEditor}
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                placeholder={replyingTo ? `Reply to ${replyingTo.pseudonym?.replace(/_/g, " ") || "someone"}...` : "Share your perspective..."}
                className="min-h-20 border-0 bg-transparent text-slate-200 placeholder:text-slate-500 focus-visible:ring-0"
              />
              <div className="flex items-center gap-2 border-t border-slate-100 px-3 py-2">
                <VoiceButton
                  testId="answer-voice"
                  supported={stt.supported}
                  listening={stt.listening}
                  onStart={() => stt.start({ onFinalText: (t) => setAnswerText((p) => p ? p + "\n\n" + t : t) })}
                  onStop={stt.stop}
                />
                {stt.listening && (
                  <span className="flex items-center gap-1 text-xs text-red-500">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
                    Recording...
                  </span>
                )}
                <div className="flex-1">
                  <InlineImageAttach
                    testIdPrefix="answer-image"
                    urls={answerImages}
                    setUrls={setAnswerImages}
                    maxItems={3}
                    onDragActive={setAnswerDragActive}
                  />
                </div>
              </div>
            </div>

            <div className="mt-3 flex justify-end gap-2">
              {replyingTo && (
                <Button
                  variant="outline"
                  onClick={cancelReply}
                  className="rounded-lg border-slate-700 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                >
                  Cancel
                </Button>
              )}
              <Button
                data-testid={tid.postAnswerButton}
                disabled={posting || answerText.trim().length < (replyingTo ? 20 : 50)}
                onClick={postAnswer}
                className="rounded-lg bg-indigo-600 font-medium text-white hover:bg-indigo-500 btn-press"
              >
                {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : replyingTo ? "Post Reply" : "Post Answer"}
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-200">Report {reportTargetType}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={reportReason} onValueChange={setReportReason}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {REPORT_REASONS.map((r) => (
                  <SelectItem key={r} value={r} className="text-slate-200 hover:bg-slate-700">{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              placeholder="Additional details (optional)"
              className="min-h-20 bg-slate-900 border-slate-700 text-slate-200 placeholder:text-slate-500"
            />
            <Button onClick={submitReport} className="w-full rounded-lg bg-red-600 text-white hover:bg-red-500">
              Submit Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Related Questions */}
      <RelatedQuestions 
        currentSlug={slug} 
        category={question.intent} 
        limit={5} 
      />
    </div>
    </div>
  );
}
